/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect } from 'react';
import { RootState, useAppDispatch } from '~/store/types';
import { validateSession } from '~/components/authentication/ducks';
import { getStatusAttribute } from '~/components/authentication/api';
import { ErrorReporterDomain } from '~/components/error-handling/error-reporter-domain';
import { reportException } from '@comeon/error-reporter';
import { SESSION_EXPIRED } from '~/components/authentication/ducks/action-names';
import { Configuration } from '~/configuration/configuration';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';

type StatusAttributeResponse = {
	result?: {
		map?: {
			nextAt: number;
		};
	};
	status?: string;
};

/**
 * React hook that periodically validates the user's session with the backend.
 *
 * When the user is authenticated, it schedules session validation requests based on the `nextAt` value
 * returned by the backend. If `nextAt` is in the past or not provided, it stops scheduling further validations
 * and dispatches a session validation action.
 */
const useSessionValidationScheduler = ({ authenticated, isInit }: { authenticated: boolean; isInit: boolean }) => {
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!authenticated) {
			return;
		}
		let timer: NodeJS.Timeout;

		const scheduleNextValidation = (nextAt: number) => {
			const now = Math.floor(Date.now() / 1000);
			if (nextAt <= now) return;

			const delay = Math.max((nextAt - now) * 1000 + 5000, 1000); // 5 seconds buffer is necessary to ensure synchronization with the backend
			timer = setTimeout(() => {
				void validate();
			}, delay);
		};

		const validate = async (isInitArg?: boolean) => {
			try {
				const sessionResponse = await dispatch(
					validateSession({ isInit: isInitArg }) as ThunkAction<
						Promise<Record<'type', string>> | undefined,
						RootState,
						Configuration,
						Action
					>
				);

				if (sessionResponse?.type === SESSION_EXPIRED) return;

				const attributeResponse = await (getStatusAttribute as () => Promise<StatusAttributeResponse>)();
				const nextAt = attributeResponse?.result?.map?.nextAt;

				if (attributeResponse.status === 'SUCCESS' && nextAt) {
					scheduleNextValidation(nextAt);
				}
			} catch (error) {
				reportException(new Error('Validate Session error', { cause: error }), {
					domain: ErrorReporterDomain.AUTHENTICATION
				});
			}
		};

		void validate(isInit);

		return () => {
			clearTimeout(timer);
		};
	}, [authenticated, isInit, dispatch]);
};

export default useSessionValidationScheduler;
